/* Importing the necessary libraries and files */
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form, FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

/* Importing the components from the material-ui library. */
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

/* Importing the sweetalert2 library. */
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

/* Importing the functions from the Redux slices. */
import { putDevice } from '../../features/device/deviceSlice';

/* Importing the functions from the utils folder. */
import handleFormResponse from '../../utils/handleFormResponse';
import { fDevice } from '../../utils/Device/Device.utils.format';
import { mDevice } from '../../utils/Device/Device.utils.map';

/* Importing the Swal library and then creating a new instance of it called MySwal. */
const MySwal = withReactContent(Swal);

/* Defining the propTypes for the TelepreseceDeleteMarker component. */
TelepreseceDeleteMarker.propTypes = {
  device: PropTypes.object,
  setDevice: PropTypes.func,
};

/**
 * Component for deleting a telepresence marker.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.device - The device object.
 * @param {Function} props.setDevice - The function to set the device object.
 * @returns {JSX.Element} The delete marker component.
 */
export default function TelepreseceDeleteMarker(props) {
  /* Destructuring the props object and assigning the isEdit property to a variable of the same name. */
  const { device, setDevice } = props;

  const [isDeleteMarkerOpen, setIsDeleteMarkerOpen] = useState(false);

  const handleDeleteMarker = () => {
    setIsDeleteMarkerOpen(true);
  };
  /* Importing the useDispatch hook from the react-redux library. */
  const dispatch = useDispatch();

  const [title] = useState('Delete Position Marker');

  const options = device.markers ? Object.keys(device.markers).map((key) => ({ name: key })) : [];

  /* Creating a schema for the form. */
  const MybeshieMarkerSchema = Yup.object().shape({
    name: Yup.string().required('Name is required and must be unique.'),
  });

  /* Using the useFormik hook to create a formik object. */
  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: MybeshieMarkerSchema,
    onSubmit: async (values) => {
      const formValues = {
        ...device,
      };
      handleClose();
      if (!formValues.markers) {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'No markers to delete.',
          showConfirmButton: true,
        });

        return;
      }

      MySwal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes',
      }).then(async (result) => {
        if (result.value) {
          const keyToDelete = values.name;
          if (Object.prototype.hasOwnProperty.call(formValues.markers, keyToDelete)) {
            delete formValues.markers[keyToDelete];
          }
          await submitPutDevice(mDevice(formValues));
        } else {
          handleDeleteMarker();
        }
      });
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, resetForm } = formik;

  /**
   * It takes the values from the form, adds the deviceID to the values, and then sends the data to the
   * putDevice function.
   */
  const submitPutDevice = async (values) => {
    const data = { deviceID: device.id, data: values };
    const resultAction = await dispatch(putDevice(data));
    handleClose();
    const payload = await handleFormResponse({
      resultAction,
      successTitle: 'Marker deleted successfully!',
      navigateOnSuccess: false,
    });
    setDevice(fDevice(payload));
    resetForm();
  };

  const handleClose = () => {
    setIsDeleteMarkerOpen(false);
  };

  return (
    <>
      <Button variant="contained" color="error" onClick={handleDeleteMarker}>
        Delete Marker
      </Button>

      <Dialog open={isDeleteMarkerOpen} onClose={handleClose}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
              <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                <Grid item xs={12} md={12}>
                  <Stack spacing={3}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ pt: 1 }}>
                        Name
                      </Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Autocomplete
                        fullWidth
                        options={options}
                        getOptionLabel={(option) => (option === null ? '' : option.name)}
                        isOptionEqualToValue={(option, value) => option.name === value.name}
                        onChange={(e, value) => {
                          if (value) {
                            formik.setFieldValue('name', value.name);
                          } else {
                            formik.setFieldValue('name', '');
                          }
                        }}
                        renderOption={(props, option) => (
                          <li {...props} key={option.name}>
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Name"
                            error={Boolean(touched.name && errors.name)}
                            helperText={touched.name && errors.name}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <LoadingButton color="error" type="submit" loading={isSubmitting}>
                Delete
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </Dialog>
    </>
  );
}
